import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from './supabase.client'

interface UserContextType {
  user: User | null // OAuth认证用户
  dbUser: any | null // 数据库中的完整用户记录
  isFullyRegistered: boolean // 是否完成完整注册
  loading: boolean
  signOut: () => Promise<void>
  refreshUserStatus: () => Promise<void> // 手动刷新用户状态
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [dbUser, setDbUser] = useState<any | null>(null)
  const [isFullyRegistered, setIsFullyRegistered] = useState(false)
  const [loading, setLoading] = useState(true)
  
  // 缓存和防抖相关
  const userCacheRef = useRef<Map<string, { user: any, timestamp: number }>>(new Map())
  const checkTimeoutRef = useRef<NodeJS.Timeout>()
  const lastCheckRef = useRef<string>('')
  
  // 检查localStorage临时session
  const checkTemporarySession = () => {
    try {
      const tempSession = localStorage.getItem('user_session')
      
      if (tempSession) {
        const session = JSON.parse(tempSession)
        
        if (session.authenticated && session.user) {
          // 检查session是否还有效（24小时）
          const sessionTime = new Date(session.timestamp).getTime()
          const now = new Date().getTime()
          const twentyFourHours = 24 * 60 * 60 * 1000
          
          if (now - sessionTime < twentyFourHours) {
            if (session.user && session.user.id) {
              setDbUser(session.user)
              setIsFullyRegistered(session.user.profile_completed || false)
              return true
            } else {
              localStorage.removeItem('user_session')
              return false
            }
          } else {
            localStorage.removeItem('user_session')
          }
        }
      }
    } catch (error) {
      localStorage.removeItem('user_session')
    }
    return false
  }

  // 缓存检查数据库用户记录
  const checkDatabaseUser = async (authUser: User | null, force: boolean = false) => {
    if (!authUser) {
      // 如果没有OAuth用户，检查是否有有效的临时session
      const hasValidTempSession = checkTemporarySession()
      if (!hasValidTempSession) {
        setDbUser(null)
        setIsFullyRegistered(false)
      }
      return
    }

    const userId = authUser.id
    const now = Date.now()
    const CACHE_DURATION = 30 * 1000 // 30秒缓存

    // 检查缓存
    if (!force) {
      const cached = userCacheRef.current.get(userId)
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        // console.log('🔄 使用缓存的用户数据:', userId)
        setDbUser(cached.user)
        setIsFullyRegistered(cached.user?.profile_completed || false)
        return
      }
    }

    // 防抖：避免重复请求
    if (lastCheckRef.current === userId && !force) {
      // console.log('⚠️ 跳过重复的用户检查请求:', userId)
      return
    }
    lastCheckRef.current = userId

    try {
      // console.log('🔍 检查数据库用户记录:', userId)
      const response = await fetch('/api/check-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId })
      })

      if (response.ok) {
        const result = await response.json() as { exists: boolean, user?: any }
        
        if (result.exists && result.user?.profile_completed) {
          setDbUser(result.user)
          setIsFullyRegistered(true)
          // 缓存结果
          userCacheRef.current.set(userId, {
            user: result.user,
            timestamp: now
          })
          // console.log('✅ 用户状态已确认：已完成注册')
        } else {
          setDbUser(null)
          setIsFullyRegistered(false)
          // console.log('⚠️ 用户状态：未完成注册或不存在')
        }
      } else {
        console.error('❌ 检查数据库用户失败:', response.status)
        setDbUser(null)
        setIsFullyRegistered(false)
      }
    } catch (error) {
      console.error('❌ 检查数据库用户请求失败:', error)
      setDbUser(null)
      setIsFullyRegistered(false)
    } finally {
      // 重置防抖标记
      setTimeout(() => {
        lastCheckRef.current = ''
      }, 1000)
    }
  }

  // 手动刷新用户状态（用于注册完成后立即更新）
  const refreshUserStatus = async () => {
    if (user) {
      // console.log('🔄 手动刷新用户状态')
      await checkDatabaseUser(user, true) // 强制刷新，忽略缓存
    }
  }

  useEffect(() => {
    // 获取初始用户状态
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      setUser(user)
      
      // 如果没有OAuth用户，检查临时session
      if (!user) {
        const hasTemporarySession = checkTemporarySession()
        if (!hasTemporarySession) {
          await checkDatabaseUser(user)
        }
      } else {
        await checkDatabaseUser(user)
      }
      
      setLoading(false)
    })
    
    // 监听自定义事件：localStorage session更新
    const handleUserSessionUpdated = () => {
      checkTemporarySession()
    }
    
    window.addEventListener('userSessionUpdated', handleUserSessionUpdated)

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const authUser = session?.user ?? null
        setUser(authUser)
        
        // 对于登录和注册事件，延迟检查以减少延迟感
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // console.log('🔄 延迟检查用户状态以优化性能')
          if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current)
          }
          checkTimeoutRef.current = setTimeout(() => {
            checkDatabaseUser(authUser)
          }, 500) // 500ms延迟
        } else {
          await checkDatabaseUser(authUser)
        }
        
        setLoading(false)
      }
    )

    return () => {
      window.removeEventListener('userSessionUpdated', handleUserSessionUpdated)
      subscription.unsubscribe()
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current)
      }
    }
  }, [])

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setDbUser(null)
    setIsFullyRegistered(false)
    // 清除缓存和临时session
    userCacheRef.current.clear()
    localStorage.removeItem('user_session')
  }

  const value = {
    user, // OAuth认证用户
    dbUser, // 数据库用户记录
    isFullyRegistered, // 是否完成完整注册
    loading,
    signOut,
    refreshUserStatus // 手动刷新
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
} 